(() => {
  'use strict';
  if (customElements.get('voice-search')) return;
  customElements.define('voice-search', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      const Speech = window.SpeechRecognition || window.webkitSpeechRecognition;
      this.button = this.querySelector('button');
      this.status = this.closest('smart-header')?.querySelector('[data-voice-status]') || this.querySelector('[data-voice-status]');
      if (!this.button) { this.hidden = true; return; }
      this.hidden = false;
      this.events = new AbortController();
      this.Speech = Speech;
      if (!Speech) this.button.setAttribute('aria-description', this.dataset.unsupported || '');
      else this.button.removeAttribute('aria-description');
      this.version ??= 0;
      this.desktop = window.matchMedia('(min-width: 990px)');
      const signal = this.events.signal;
      this.setListening(false);
      this.showStatus('', '');
      document.addEventListener('olea:header-panel-open', event => {
        if (!this.listening) return;
        if (event.detail?.source !== this.targetInput?.closest('predictive-search')) this.cancelListening();
        else this.searchPanelWasOpen = true;
      }, { signal });
      document.addEventListener('olea:header-panel-close', () => this.cancelListening(), { signal });
      document.addEventListener('keydown', event => { if (event.key === 'Escape') this.cancelListening(); }, { signal });
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.cancelListening(); }, { signal });
      document.addEventListener('pointerdown', event => {
        if (!this.button.contains(event.target)) this.allowMicroFocusout = false;
        if (this.listening && !this.closest('smart-header')?.contains(event.target)) this.cancelListening();
      }, { signal });
      this.panelObserver = new MutationObserver(() => {
        if (!this.listening || !this.searchPanelWasOpen || document.documentElement.dataset.headerPanel === 'search') return;
        if (this.allowMicroFocusout || document.activeElement === this.button) {
          this.searchPanelWasOpen = false;
          this.allowMicroFocusout = false;
        }
        else this.cancelListening();
      });
      this.panelObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-header-panel'] });
      this.desktop.addEventListener('change', () => this.cancelListening(), { signal });
      this.button.addEventListener('click', () => {
        if (this.listening) return this.cancelListening();
        this.allowMicroFocusout = true;
        this.startListening();
      }, { signal });
    }
    startListening() {
      if (!this.Speech) { this.showStatus('unsupported', this.dataset.unsupported || ''); return; }
      const input = this.closest('smart-header')?.querySelector('.header-search-desktop input[type=search]');
      if (!input?.isConnected || !input.getClientRects().length) return;
      let speech;
      try { speech = new this.Speech(); }
      catch (error) { this.showFailure(error); return; }
      const version = ++this.version;
      this.speech = speech;
      this.targetInput = input;
      this.searchPanelWasOpen = document.documentElement.dataset.headerPanel === 'search';
      speech.lang = document.documentElement.lang || 'fr-FR';
      speech.interimResults = false;
      speech.maxAlternatives = 1;
      const current = () => this.speech === speech && this.version === version && this.listening && !this.events?.signal.aborted;
      speech.onresult = event => {
        if (!current() || !input.isConnected || !input.getClientRects().length) return;
        const transcript = event.results[0]?.[0]?.transcript;
        const value = typeof transcript === 'string' ? transcript.trim() : '';
        if (!value) { this.showFailure({ error: 'no-speech' }); return; }
        this.cancelListening();
        input.value = value;
        input.focus();
        input.dispatchEvent(new Event('input', { bubbles: true }));
      };
      speech.onend = () => {
        if (!current()) return;
        this.cancelListening(false);
        this.showStatus('noMatch', this.dataset.noMatch || this.dataset.error || '');
      };
      speech.onnomatch = () => { if (current()) this.showFailure({ error: 'no-speech' }); };
      speech.onerror = error => { if (current()) this.showFailure(error); };
      this.setListening(true);
      this.showStatus('listening', this.dataset.listening || '');
      try { speech.start(); }
      catch (error) { this.showFailure(error); }
    }
    cancelListening(abort = true) {
      const speech = this.speech;
      this.version++;
      this.speech = null;
      this.targetInput = null;
      this.searchPanelWasOpen = false;
      this.allowMicroFocusout = false;
      this.setListening(false);
      this.showStatus('', '');
      if (!speech) return;
      speech.onresult = speech.onend = speech.onerror = speech.onnomatch = null;
      if (abort) { try { speech.abort(); } catch {} }
    }
    showStatus(state, message) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.hidden = !message;
      this.status.dataset.state = state;
    }
    showFailure(reason) {
      const code = String(reason?.error || reason?.name || '').toLowerCase();
      this.cancelListening();
      if (code === 'aborted') return;
      const key = ['not-allowed', 'service-not-allowed', 'notallowederror', 'securityerror'].includes(code) ? 'denied' : code === 'no-speech' ? 'noMatch' : 'error';
      this.showStatus(key, this.dataset[key] || this.dataset.error || '');
    }
    setListening(value) { this.listening = value; this.button?.setAttribute('aria-pressed', String(value)); }
    disconnectedCallback() { this.cancelListening(); this.events?.abort(); this.events = null; this.panelObserver?.disconnect(); this.panelObserver = null; }
  });
})();
